import { errorText } from '../../errorText';
import { useBackForward } from '../../useBackForward';
import { useState, useCallback } from 'react';
import { Flex, Button, Text, Callout } from '@dodilio/ui-design-lib';
import { ServiceRegister } from '../../api';
import { auth } from '../../auth';
import { leaveWithSession } from '../../handoff';
import { GetInviteResponse } from '../../types/register';

/**
 * Accepting as somebody who ALREADY has an account — joining a second
 * organization.
 *
 * No password field, deliberately. The server refuses to touch an existing
 * account's password on this path: the invite code is readable by whoever
 * created the invite, so if a password could be set here, an org admin could
 * invite a user's address and take the account over.
 */
export const AcceptAsExistingUser = ({
    code,
    invite,
    token,
}: {
    code: string;
    invite: GetInviteResponse;
    token: string;
}) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);
    // Back from the console: Join works again.
    useBackForward(useCallback(() => { setBusy(false); setDone(false); }, []));
    const redirect = import.meta.env.VITE_DEFAULT_REDIRECT;

    const accept = async () => {
        setBusy(true);
        setError(null);
        try {
            await ServiceRegister.joinOrganization({ code, password: '' }, token);
            setDone(true);
            // A token names its organization: mint a fresh one before the console reads it.
            await auth.forceRefresh().catch(() => false);
            // Into the organization just joined, not the one the session was in before.
            await leaveWithSession({ target: redirect, organizationName: invite.organizationName });
        } catch (e) {
            setError(errorText(e, 'invite', 'Could not accept the invitation.'));
            setBusy(false);
        }
    };

    if (done) {
        return (
            <Callout.Root color="green" size="2">
                <Callout.Text>You&apos;ve joined {invite.organizationDisplayName || invite.organizationName}. Redirecting…</Callout.Text>
            </Callout.Root>
        );
    }

    return (
        <Flex direction="column" gap="3">
            {error && (
                <Callout.Root color="red" size="1" role="alert">
                    <Callout.Text>{error}</Callout.Text>
                </Callout.Root>
            )}
            <Text size="2">
                You&apos;ve been invited to join <strong>{invite.organizationDisplayName || invite.organizationName}</strong>
                {invite.invitedEmail ? <> as {invite.invitedEmail}</> : null}.
            </Text>
            <Text size="1" color="gray">
                You already have a Dodil account, so there&apos;s nothing to set up — this
                just adds the organization. Your password doesn&apos;t change.
            </Text>
            <Button radius="full" size="2" variant="classic" disabled={busy} loading={busy} onClick={accept}>
                Join {invite.organizationDisplayName || invite.organizationName}
            </Button>
        </Flex>
    );
};
